import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';

import { QuestionService } from 'src/app/common/question-dialog/question-dialog.service';
import { LoaderService } from 'src/app/common/loader/loader.service';
import { InfoboxService } from 'src/app/common/infobox/infobox.service';
import { Data2Service } from 'src/app/common/data2.service';
import { UuidService } from 'src/app/common/uuid.service';
import { BankCardPosition } from '../bank.model';
import { TourismEvent } from '../../tourism-events/tourism-events.model';
import { environment } from '../../../environments/environment';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
	selector: 'app-bank-transaction-split',
	templateUrl: './bank-transaction-split.component.html',
	styleUrls: ['./bank-transaction-split.component.css'],
})
export class BankTransactionSplitComponent implements OnInit {
	pageTitle = '';
	yesNoQuestion = '';
	yesNoId = '';
	data = {
		id: null,
		name: null,
		value: null,
		quantity: null,
		currency: null,
		comment: null,
		linkEvents: null,
		currentOwnerName: null,
		dateoperation: null,
	};
	positions: BankCardPosition[] = [];
	types: { id: string; name: string }[] = [];
	scrollToId = '';
	tourismEventControls = new Map<string, FormControl<TourismEvent | string | null>>();
	filteredTourismEvents = new Map<string, TourismEvent[]>();
	private itemsBeforeDataLoad = 2;
	private readonly http = inject(HttpClient);

	constructor(
		private readonly api: Data2Service,
		private readonly infobox: InfoboxService,
		private readonly uuid: UuidService,
		private readonly question: QuestionService,
		private readonly loader: LoaderService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly destroyRef: DestroyRef,
	) {}

	ngOnInit(): void {
		if (!this.route.snapshot.params['id']) {
			this.router.navigate(['bankTransactions']);
			return;
		}
		this.loader.show();
		this.data.id = this.route.snapshot.params['id'];
		this.api.get('/bankTransactionsTypes/', (result, error) => {
			this.types = result.data;
			this.loadData();
		});
		this.api.get(`/bankCardsHistory/${encodeURIComponent(this.data.id)}/`, (result, error) => {
			if (error) {
				return;
			}
			this.data = result.data;
			this.scrollToId = this.data.id;
			this.pageTitle =
				this.data.currentOwnerName +
				' - ' +
				this.data.name +
				' = ' +
				this.data.value +
				' ' +
				this.data.currency +
				' (' +
				this.data.dateoperation +
				')';
			this.loadData();
		});
	}

	initializeTourismEventControl(position: BankCardPosition): void {
		const control = new FormControl<TourismEvent | string | null>(null);
		this.tourismEventControls.set(position.id, control);
		this.filteredTourismEvents.set(position.id, []);

		if (position.linkEvents) {
			this.fetchTourismEventById(position.linkEvents).subscribe((event) => {
				if (event) {
					const displayName = event.date_from ? `${event.name} (${event.date_from})` : event.name;
					control.setValue(displayName, { emitEvent: false });
					position.tourismEventName = displayName;
				}
			});
		}

		control.valueChanges
			.pipe(
				debounceTime(300),
				distinctUntilChanged(),
				filter((value) => typeof value === 'string' && value.length >= 3),
				switchMap((query) => this.searchTourismEvents(query as string)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((events) => {
				this.filteredTourismEvents.set(position.id, events);
			});
	}

	searchTourismEvents(query: string) {
		return this.http
			.get<{
				data: TourismEvent[];
			} | null>(
				`${environment.erpUrl}/tourismEvents/?request_type=autocomplete&search=${encodeURIComponent(query)}`,
			)
			.pipe(map((response) => (response && response.data ? response.data : [])));
	}

	fetchTourismEventById(eventId: string) {
		return this.http
			.get<{ data: TourismEvent } | null>(`${environment.erpUrl}/tourismEvents/${encodeURIComponent(eventId)}/`)
			.pipe(map((response) => (response && response.data ? response.data : null)));
	}

	getTourismEventControl(positionId: string): FormControl<TourismEvent | string | null> {
		return this.tourismEventControls.get(positionId) || new FormControl<TourismEvent | string | null>(null);
	}

	getFilteredTourismEvents(positionId: string): TourismEvent[] {
		return this.filteredTourismEvents.get(positionId) || [];
	}

	displayTourismEventFn(event: TourismEvent | string): string {
		if (!event || typeof event === 'string') {
			return event as string;
		}
		return event.date_from ? `${event.name} (${event.date_from})` : event.name || '';
	}

	onTourismEventSelected(event: MatAutocompleteSelectedEvent, position: BankCardPosition): void {
		const selectedEvent = event.option.value as TourismEvent;
		const displayName = selectedEvent?.date_from
			? `${selectedEvent.name} (${selectedEvent.date_from})`
			: selectedEvent?.name || null;
		position.tourismEventName = displayName;
		position.linkEvents = selectedEvent?.id || null;
	}

	save(): void {
		this.positions = this.positions.filter((obj) => obj.value || obj.type);
		let sum = 0;
		this.positions.forEach((item) => {
			sum += parseFloat(item.value);
		});
		if (sum.toFixed(2) != parseFloat(this.data.value).toFixed(2)) {
			this.infobox.error('Suma kwot jest niezgodna');
			return;
		}
		this.api.put(`/bankTransactions/${encodeURIComponent(this.data.id)}/`, this.positions, (result, error) => {
			if (error) {
				return;
			}
		});
	}

	addPosition(): void {
		const data: BankCardPosition = {
			id: this.uuid.generate(),
			bankCardsHistory: this.data.id,
			value: null,
			verified: 0,
			type: null,
			comment: null,
			linkEvents: null,
			tourismEventName: null,
			tourismEventIdName: null,
		};
		this.positions.push(data);
		this.initializeTourismEventControl(data);
	}

	deleteAsk(item: { id: string }): void {
		if (this.positions.length === 1) {
			this.infobox.error('Nie można usunąć jedynej pozycji');
			return;
		}
		const dialogId = 'deleteAsk';
		this.question.ask('Czy na pewno usunąć?', dialogId);
		this.question.status.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
			if (result.dialogId !== dialogId) {
				return;
			}
			if (!result.result) {
				return;
			}
			this.positions = this.positions.filter((obj) => obj.id != item.id);
		});
	}

	private loadData(): void {
		this.itemsBeforeDataLoad--;
		if (this.itemsBeforeDataLoad > 0) {
			return;
		}
		this.loader.show();
		this.api.get(
			`/bankCardsHistoryPositions/?bankCardsHistory=${encodeURIComponent(this.data.id)}`,
			(result, error) => {
				if (error) {
					return;
				}
				this.positions = result.data;
				this.positions.forEach((position) => {
					this.initializeTourismEventControl(position);
				});
				this.loader.hide();
			},
		);
	}
}
